import {
  InlineDatumReactComponentProps,
  useInlineDataEvents,
} from '@stripe-internal/inline-data';
import {view, Icon} from '@sail/ui';
import {warning, info, chat, call, email} from '@sail/icons/react/Icon';
import {IconAsset} from '@sail/icons/types';
import {v4 as uuid} from 'uuid';
import {filter} from 'rxjs';

import {useCallback, useEffect, useMemo, useState} from 'react';
import {ChatButtonOptions} from '@stripe-internal/conversations-ui';
import {defineMessages, useIntl} from 'react-intl';

const messages = defineMessages({
  restartTheChat: {
    id: 'mse_inline_data.universal_help_channel_menu.restart_the_chat',
    description: 'Button used to restart the stripe support chat.',
    defaultMessage: 'Ask about something else',
  },
  sendError: {
    id: 'mse_inline_data.universal_help_channel_menu.send_error',
    description: 'Error when there is an issue attempting to a send a message',
    defaultMessage:
      'There was an issue selecting your option. Please check your internet connection and try again.',
  },
});

const POLL_MS = 3000;

type ChannelOption = {
  id: string;
  message: string;
  disabled?: boolean;
  icon?: string;
};

type ValueProps = {
  channel_options: Array<ChannelOption>;
  notice?: string;
  notice_type?: string;
  selected_option_id?: string;
  hide_restart_chat_option?: boolean;
  pause_polling?: boolean;
};

const getIconFromName = (iconName: string): IconAsset | undefined => {
  switch (iconName) {
    case 'chat':
      return chat;
    case 'call':
      return call;
    case 'email':
      return email;
    default:
      return undefined;
  }
};

export function UniversalHelpChannelMenuLegacy(
  props: InlineDatumReactComponentProps,
) {
  const intl = useIntl();

  const {datum, onDone, isDone, fetch, action} = props;
  const value = datum.value as ValueProps;
  const {
    channel_options: channelOptions,
    notice,
    notice_type: noticeType,
    selected_option_id: selectedOptionId,
    hide_restart_chat_option: hideRestartChatOption,
    pause_polling: pausePolling,
  } = value;
  const [restartChatOptionDisabled] = useState<boolean>(
    !!hideRestartChatOption,
  );

  const events = useInlineDataEvents();

  const nonce = uuid();

  const [selectedOption, setSelectedOption] = useState<string | undefined>();

  const [err, setErr] = useState<string | undefined>();

  const [conversationActiveChannel, setConversationActiveChannel] = useState<
    string | undefined
  >();

  // Update the selected option if we are provided one from the props
  useEffect(() => {
    if (selectedOptionId) {
      setSelectedOption(selectedOptionId);

      if (isDone) {
        return;
      }

      onDone({
        type: 'success',
        message: selectedOption,
        extra: {
          outcome: selectedOption,
          value: {
            ...value,
            selected_option_id: selectedOption,
          },
        },
      });
    }
  }, [isDone, onDone, selectedOption, selectedOptionId, value]);

  useEffect(() => {
    if (isDone || pausePolling) {
      return;
    }

    const timeout = setTimeout(() => {
      if (!isDone) {
        fetch({
          poll: true,
        });
      }
    }, POLL_MS);

    return () => clearTimeout(timeout);
  }, [isDone, fetch, pausePolling]);

  useEffect(() => {
    if (isDone) {
      return;
    }

    const sub = events.observable
      .pipe(
        filter(
          (e) => e.name === 'inline-data-action-result' && e.nonce === nonce,
        ),
      )
      .subscribe((event) => {
        if (event.data?.channelID) {
          const channel = event.data?.channelID as string;

          if (['channel_chat', 'channel_restart'].includes(channel)) {
            if (event.error) {
              setErr(intl.formatMessage(messages.sendError));
            } else {
              action({selected_option_id: channel});
              // Immediately set the option so that UI updates without having to wait for action and onDone to complete
              setSelectedOption(channel);
            }
          }
        }
      });

    return () => sub.unsubscribe();
  }, [action, events, intl, isDone, nonce, onDone]);

  useEffect(() => {
    const sub = events.observable
      .pipe(filter((e) => e.name === 'conversation-channel-updated'))
      .subscribe((e) => {
        if (isDone) {
          return;
        }

        if (typeof e.data?.channel === 'string') {
          setConversationActiveChannel(e.data.channel);
        }
      });

    return () => sub.unsubscribe();
  }, [events, isDone]);

  const selectOption = useCallback(
    (option: ChannelOption) => {
      events.emit({
        name: 'inline-data-action',
        data: {
          purpose: 'channel_selection',
          channelID: option.id,
        },
        nonce,
      });
    },
    [events, nonce],
  );

  const convOptions = channelOptions
    .concat({
      id: 'channel_restart',
      message: intl.formatMessage(messages.restartTheChat),
      disabled: !!restartChatOptionDisabled,
    })
    .filter((option) => !option.disabled)
    .map((option) => {
      return {
        value: option.id,
        label: option.message,
        onPress: () => selectOption(option),
        icon: option.icon ? getIconFromName(option.icon) : undefined,
      };
    });

  // TODO(CONVO_UX-4739): Reimplement the channel selector to be more aware of conversation context
  // and work better with the upcoming inline contact flows
  const currentChannel = useMemo(() => {
    if (conversationActiveChannel === 'request_a_call') {
      return 'channel_rac';
    } else if (conversationActiveChannel === 'email') {
      return 'channel_email';
    }

    return selectedOption;
  }, [conversationActiveChannel, selectedOption]);

  return (
    <view.div
      css={{
        margin: 'small',
        stack: 'y',
        gap: 'medium',
      }}
    >
      {notice && noticeType ? (
        <view.div
          role="alert"
          css={{
            stack: 'x',
            alignY: 'center',
            gap: 'small',
            color: noticeType === 'warning' ? 'attention' : 'info',
          }}
        >
          <Icon
            icon={noticeType === 'warning' ? warning : info}
            size="xsmall"
            aria-hidden
            css={{
              alignSelf: 'baseline',
              flexShrink: '0',
              paddingTop: 'xxsmall',
              marginTop: 'xxsmall',
            }}
          />
          <view.div>{notice}</view.div>
        </view.div>
      ) : null}

      <view.div css={{alignX: 'end', stack: 'y', gap: 'small'}}>
        <ChatButtonOptions
          options={convOptions}
          selectedOptionId={currentChannel}
          error={err}
        />
      </view.div>
    </view.div>
  );
}
